import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { PREFIX_URL } from '@/src/api/config';
import { showToast } from '@/src/globalFunction';
import { axios } from '@/src/lib/axios/axios';

import { INBOX_API_KEY } from '../../Inbox2/api/config';
import { All_FILES_API_KEY } from '../../Inbox2/components/FilesView/api/getAllFiles';
import { getRootFilesQueryKey } from '../../Library/api/getRootFiles';
import { queryClient } from '@/src/lib/react-query/react-query';
import { fileDetailsKeys } from '../../Inbox2/components/EntryPanel/FileEntryViewer/api/getFileDetails';
import { getFilesQueryKey } from '../../Library/api/getFiles';

export interface ApiResponse {
  success: boolean;
  data: ApplicationTagDto[];
}

export interface ApplicationTagDto {
  id: number;
  name: string;
  isSystem: boolean;
  color: string;
  version: number;
}

const baseUrl = `${PREFIX_URL}/projects`;
const apiBaseUrl = `${PREFIX_URL}`;

// --- Project Tags ---
export const PROJECT_TAGS_KEY = 'projectTags';

export const getProjectTags = (projectId: number | string): Promise<ApplicationTagDto[]> => {
  return axios.get(`${baseUrl}/${projectId}/tags`).then((res) => res.data);
};

export const createProjectTag = (
  projectId: number,
  body: { name: string; color: string }
): Promise<ApplicationTagDto> => {
  return axios.post(`${baseUrl}/${projectId}/tags`, body).then((res) => res.data.data);
};

export const updateProjectTag = (
  tagId: number | string,
  body: { name?: string; color?: string; version: number }
): Promise<ApplicationTagDto> => {
  return axios.put(`${apiBaseUrl}/tags/${tagId}`, body).then((res) => res.data.data);
};

export const deleteProjectTag = (
  tagId: number,
  body: { version: number }
): Promise<{ success: true }> => {
  return axios.delete(`${apiBaseUrl}/tags/${tagId}`, { data: body });
};

export const useGetProjectTags = (projectId: number | string, options = {}) => {
  return useQuery({
    queryKey: [PROJECT_TAGS_KEY, projectId],
    queryFn: () => getProjectTags(projectId),
    select: (data) => data, // ensure the array is returned directly
    staleTime: Infinity,
    ...options,
  });
};

export const useCreateProjectTag = (projectId: number, options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; color: string }) => createProjectTag(projectId, body),
    onSuccess: () => {
      showToast('success', 'Tag created successfully');
      queryClient.invalidateQueries({ queryKey: [PROJECT_TAGS_KEY] });
      queryClient.invalidateQueries({ queryKey: [INBOX_API_KEY, ...fileDetailsKeys.all] });
      queryClient.invalidateQueries({ queryKey: [...getRootFilesQueryKey.list] });
      queryClient.invalidateQueries({ queryKey: [All_FILES_API_KEY] });
    },
    ...options,
  });
};

export const useUpdateProjectTag = (projectId: number | string, options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      tagId: number | string;
      body: { name?: string; color?: string; version: number };
    }) => updateProjectTag(params.tagId, params.body),
    onSuccess: () => {
      showToast('success', 'Tag updated successfully');
      queryClient.invalidateQueries({ queryKey: [PROJECT_TAGS_KEY] });
      queryClient.invalidateQueries({ queryKey: [INBOX_API_KEY, ...fileDetailsKeys.all] });
      queryClient.invalidateQueries({ queryKey: [...getRootFilesQueryKey.list] });
      queryClient.invalidateQueries({ queryKey: [All_FILES_API_KEY] });
    },
    onError: (e: any) => {
      const message = e?.response?.data?.message || e?.message || 'Failed to update tag';
      showToast('error', message);
    },
    ...options,
  });
};

export const useDeleteProjectTag = (projectId: number | string, options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { tagId: number; version: number }) =>
      deleteProjectTag(params.tagId, { version: params.version }),
    onSuccess: () => {
      showToast('success', 'Tag deleted successfully');
      queryClient.invalidateQueries({ queryKey: [PROJECT_TAGS_KEY] });
      queryClient.invalidateQueries({ queryKey: [INBOX_API_KEY, ...fileDetailsKeys.all] });
      queryClient.invalidateQueries({ queryKey: [...getRootFilesQueryKey.list] });
      queryClient.invalidateQueries({ queryKey: [All_FILES_API_KEY] });
      queryClient.invalidateQueries({ queryKey: [...getFilesQueryKey.list] });
    },
    onError: (e: any) => {
      const message = e?.response?.data?.message || e?.message || 'Failed to delete tag';
      showToast('error', message);
    },
    ...options,
  });
};

// --- Inbox Document Tags ---
export const getInboxDocumentTags = (
  inboxDocumentId: number | string
): Promise<ApplicationTagDto[]> => {
  return axios.get(`/api/inbox-documents/${inboxDocumentId}/tags`).then((res) => res.data);
};

export const attachInboxDocumentTag = (
  inboxDocumentId: number | string,
  tagId: number
): Promise<{ success: true }> => {
  return axios.put(`/api/inbox-documents/${inboxDocumentId}/tags/${tagId}`);
};

export const detachInboxDocumentTag = (
  inboxDocumentId: number | string,
  tagId: number
): Promise<{ success: true }> => {
  return axios.delete(`/api/inbox-documents/${inboxDocumentId}/tags/${tagId}`);
};

export const useGetInboxDocumentTags = (inboxDocumentId: number | string, options = {}) => {
  return useQuery({
    queryKey: ['inboxDocumentTags', inboxDocumentId],
    queryFn: () => getInboxDocumentTags(inboxDocumentId),
    ...options,
  });
};

export const useAttachInboxDocumentTag = (inboxDocumentId: number, options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tagId: number) => attachInboxDocumentTag(inboxDocumentId, tagId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inboxDocumentTags', inboxDocumentId] });
      queryClient.invalidateQueries({ queryKey: [INBOX_API_KEY, ...fileDetailsKeys.all] });
      queryClient.invalidateQueries({ queryKey: [PROJECT_TAGS_KEY] });
      queryClient.invalidateQueries({ queryKey: [All_FILES_API_KEY] });
      queryClient.invalidateQueries({ queryKey: [...getRootFilesQueryKey.list] });
    },
    ...options,
  });
};

export const useDetachInboxDocumentTag = (inboxDocumentId: number | string, options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tagId: number) => detachInboxDocumentTag(inboxDocumentId, tagId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inboxDocumentTags', inboxDocumentId] });
      queryClient.invalidateQueries({ queryKey: [INBOX_API_KEY, ...fileDetailsKeys.all] });
      queryClient.invalidateQueries({ queryKey: [PROJECT_TAGS_KEY] });
      queryClient.invalidateQueries({ queryKey: [All_FILES_API_KEY] });
      queryClient.invalidateQueries({ queryKey: [...getRootFilesQueryKey.list] });
    },
    ...options,
  });
};

// --- Library Folder Tags ---
export const getLibraryFolderTags = (folderId: number): Promise<ApplicationTagDto[]> => {
  return axios.get(`/api/library/${folderId}/tags`).then((res) => res.data);
};

export const attachLibraryFolderTag = (
  folderId: number,
  tagId: number
): Promise<{ success: true }> => {
  return axios.put(`/api/library/${folderId}/tags/${tagId}`);
};

export const detachLibraryFolderTag = (
  folderId: number,
  tagId: number
): Promise<{ success: true }> => {
  return axios.delete(`/api/library/${folderId}/tags/${tagId}`);
};

export const useGetLibraryFolderTags = (folderId: number, options = {}) => {
  return useQuery({
    queryKey: ['libraryFolderTags', folderId],
    queryFn: () => getLibraryFolderTags(folderId),
    ...options,
  });
};

export const useAttachLibraryFolderTag = (folderId: number, options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tagId: number) => attachLibraryFolderTag(folderId, tagId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...getRootFilesQueryKey.list] });
      queryClient.invalidateQueries({ queryKey: [...getFilesQueryKey.list] });
      queryClient.invalidateQueries({ queryKey: [INBOX_API_KEY, ...fileDetailsKeys.all] });
      queryClient.invalidateQueries({ queryKey: [All_FILES_API_KEY] });
    },
    ...options,
  });
};

export const useDetachLibraryFolderTag = (folderId: number, options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tagId: number) => detachLibraryFolderTag(folderId, tagId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...getRootFilesQueryKey.list] });
      queryClient.invalidateQueries({ queryKey: [...getFilesQueryKey.list] });
      queryClient.invalidateQueries({ queryKey: [INBOX_API_KEY, ...fileDetailsKeys.all] });
      queryClient.invalidateQueries({ queryKey: [All_FILES_API_KEY] });
    },
    ...options,
  });
};

export const invalidateInboxQueries = () => {
  queryClient.invalidateQueries({ queryKey: [INBOX_API_KEY, ...fileDetailsKeys.all] });
};

export const invalidateLibraryQueries = () => {
  queryClient.invalidateQueries({ queryKey: [...getRootFilesQueryKey.list] });
};

export const invalidateAllFilesQueries = () => {
  queryClient.invalidateQueries({ queryKey: [All_FILES_API_KEY] });
};

export const invalidateProjectQueries = () => {
  queryClient.invalidateQueries({ queryKey: [PROJECT_TAGS_KEY] });
};

export const invalidateAllInboxQueries = () => {
  queryClient.invalidateQueries({ queryKey: [INBOX_API_KEY] });
};

export const invalidateLibraryFolderQueries = () => {
  queryClient.invalidateQueries({ queryKey: ['libraryFolderTags'] });
};

export const invalidateFilesQuery = () => {
  queryClient.invalidateQueries({ queryKey: [...getFilesQueryKey.list] });
};
